import React, { useState } from 'react';
import { X, Mail, Copy, Check } from 'lucide-react';
import { inviteAggregatorTeamMember } from '../../services/api';
import type { AggregatorRole } from '../../types';

interface Props {
  onClose: () => void;
  onSuccess: () => void;
  /**
   * 'modal' (default) renders the original fixed-overlay dialog. 'inline'
   * (Fig60) renders the same real form + submit logic as a persistent card —
   * used by TeamManagement's always-visible "Invite a member" side panel, so
   * the validation/submit/invite-link/copy behaviour is never duplicated.
   */
  variant?: 'modal' | 'inline';
  /** Bug #5858 — Figure 13's "Invite as Analyst →" lands here with the role
   * preselected and the email field focused. */
  initialRole?: InviteRole;
  autoFocusEmail?: boolean;
}

type InviteRole = Exclude<AggregatorRole, 'billing_owner'>;

// A visibly incomplete email (no '@', or nothing after a trailing '.') gets an
// honest inline warning instead of only relying on the native `required`
// pop-up — the inline variant has no browser validation bubble to lean on.
function emailLooksIncomplete(value: string): boolean {
  if (!value) return false;
  return !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

export function InviteTeamMemberDialog({ onClose, onSuccess, variant = 'modal', initialRole = 'analyst', autoFocusEmail = false }: Props) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<InviteRole>(initialRole);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const emailInvalid = emailLooksIncomplete(email);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const result = await inviteAggregatorTeamMember(email, role);
      setInviteLink(result.invite_link);
      onSuccess();
    } catch (err: unknown) {
      if (err instanceof Error && err.message.includes('402')) {
        setError('This portfolio cannot add another team member right now. The billing owner can raise the limit.');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to send invite.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (!inviteLink) return;
    navigator.clipboard.writeText(inviteLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // For the inline (persistent) panel, "Done"/"Cancel" resets the form so it
  // is ready for the next invite rather than trying to dismiss an overlay
  // that, in this variant, was never there.
  const handleDone = () => {
    setInviteLink(null);
    setEmail('');
    setError(null);
    onClose();
  };

  const inline = variant === 'inline';

  const body = (
    <>
      {!inline && (
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Invite Team Member</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100" aria-label="Close">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
      )}
      {inline && <h2 className="mb-4 text-sm font-semibold text-gray-800">+ Invite a member</h2>}

      {inviteLink ? (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">Invitation created. Share this link with <strong>{email}</strong>:</p>
          <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <span className="flex-1 text-xs text-gray-700 truncate font-mono">{inviteLink}</span>
            <button
              onClick={handleCopy}
              className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 font-medium"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <button
            onClick={handleDone}
            className="w-full py-2 px-4 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700"
          >
            {inline ? 'Invite another' : 'Done'}
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email address</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                type="email"
                autoFocus={autoFocusEmail}
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="colleague@example.com"
                data-testid="invite-email-input"
                className={`w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 ${
                  emailInvalid ? 'border-red-300 focus:ring-red-400' : 'border-gray-300 focus:ring-indigo-500'
                }`}
              />
            </div>
            {emailInvalid && (
              <p className="mt-1 text-xs text-red-600" data-testid="invite-email-error">
                Enter a valid email address — it looks incomplete (e.g. name@example.com).
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            {inline ? (
              <select
                value={role}
                onChange={e => setRole(e.target.value as InviteRole)}
                data-testid="invite-role-select"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="analyst">Analyst</option>
                <option value="admin">Admin</option>
              </select>
            ) : (
              <div className="flex gap-3">
                {(['analyst', 'admin'] as InviteRole[]).map(r => (
                  <label key={r} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="role"
                      value={r}
                      checked={role === r}
                      onChange={() => setRole(r)}
                      className="text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="text-sm text-gray-700 capitalize">{r}</span>
                  </label>
                ))}
              </div>
            )}
            <p className="mt-1 text-xs text-gray-500">
              {role === 'admin'
                ? 'Admins can invite members and manage project visibility.'
                : 'Analysts can view projects assigned to them.'}
            </p>
          </div>

          {/* Fig60: segment assignment is real, but only AFTER acceptance —
              POST /aggregator-team/invite takes only email + role, there is no
              visibility parameter to set here. Saying otherwise would be a
              fabricated control, so this stays an honest pointer to the real
              per-analyst editor instead of a checkbox list that submits
              nowhere. */}
          {inline && role === 'analyst' && (
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Visible segments</label>
              <p className="text-xs text-gray-500">
                New analysts start with visibility into every cohort venture. Once they accept, use "Manage" next to
                their name below to limit which ventures they can see.
              </p>
            </div>
          )}

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
          )}

          <div className={inline ? '' : 'flex gap-3 pt-1'}>
            {!inline && (
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-2 px-4 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              disabled={loading || !email || emailInvalid}
              data-testid="invite-submit"
              className={`${inline ? 'w-full' : 'flex-1'} py-2 px-4 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {loading ? 'Sending…' : 'Send Invite'}
            </button>
            {inline && !email && (
              <p className="mt-1.5 text-center text-xs text-gray-400">Disabled until a valid email is entered.</p>
            )}
          </div>
        </form>
      )}
    </>
  );

  if (inline) {
    return (
      <div className="light-card p-6" data-testid="invite-member-panel">
        {body}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">{body}</div>
    </div>
  );
}
